<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline, vue/multiline-html-element-content-newline */
import { ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LemitAPI from 'dashboard/api/lemit';
import { useAccount } from 'dashboard/composables/useAccount';
import { formatCpf } from 'shared/helpers/talkviaInputMasks';

const emit = defineEmits(['result']);
const { accountScopedRoute } = useAccount();
const dialog = ref(null);
const cpf = ref('');
const rows = ref([]);
const loading = ref(false);
const consulting = ref('');
const error = ref('');
const result = ref(null);
const providers = [
  {
    id: 'confirme_online',
    name: 'Talkvia Cadastro',
    description:
      'Nome, data de nascimento e nome da mãe. Franquia mensal gratuita.',
  },
  {
    id: 'lemit',
    name: 'Talkvia Consulta Plus',
    description: 'Dados cadastrais, telefones e endereços disponíveis.',
  },
  {
    id: 'nova_era',
    name: 'Talkvia Contatos',
    description: 'Dados cadastrais e celulares disponíveis.',
  },
];
const canConsult = row =>
  row.configured &&
  row.product &&
  row.balance >= row.product.credits_per_use &&
  !row.error;
const number = value => Number(value || 0).toLocaleString('pt-BR');

const load = async () => {
  loading.value = true;
  rows.value = await Promise.all(
    providers.map(async provider => {
      try {
        const { data } = await LemitAPI.summary(provider.id);
        return { ...provider, ...data };
      } catch (failure) {
        return {
          ...provider,
          error:
            failure.response?.status === 403
              ? 'Seu perfil não tem acesso à higienização. Solicite ao administrador.'
              : 'Não foi possível carregar o saldo. Tente novamente.',
        };
      }
    })
  );
  loading.value = false;
};
const open = async value => {
  if (consulting.value || loading.value) return;
  cpf.value = value;
  result.value = null;
  error.value = '';
  dialog.value.open();
  await load();
};
const consult = async row => {
  if (consulting.value || !canConsult(row)) return;
  consulting.value = row.id;
  error.value = '';
  result.value = null;
  try {
    const { data } = await LemitAPI.consult(cpf.value, row.id, 'clt');
    result.value = data.pessoa;
    emit('result', { cpf: cpf.value, person: data.pessoa });
  } catch (failure) {
    error.value =
      failure.response?.data?.error ||
      'Não foi possível concluir a consulta. Confira o saldo e tente novamente.';
  } finally {
    await load();
    consulting.value = '';
  }
};
defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    title="Consultar nos birôs"
    :description="`CPF ${formatCpf(cpf)} · Escolha onde consultar os dados do cliente.`"
    width="2xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    :close-on-click-outside="!consulting"
    overflow-y-auto
    sticky-header-footer
  >
    <div class="grid gap-3">
      <p class="text-sm text-n-slate-11">
        Abrir esta janela não consome créditos. A consulta só começa ao clicar
        em Consultar. Os dados encontrados preenchem os campos vazios do
        formulário.
      </p>
      <p v-if="loading" role="status" class="text-sm text-n-slate-11">
        Atualizando saldos…
      </p>
      <article
        v-for="row in rows"
        :key="row.id"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
      >
        <h3 class="text-base font-semibold text-n-slate-12">{{ row.name }}</h3>
        <p class="mt-1 text-sm text-n-slate-11">{{ row.description }}</p>
        <p v-if="row.error" class="mt-2 text-sm text-n-ruby-11">
          {{ row.error }}
        </p>
        <template v-else>
          <div class="my-3 flex flex-wrap gap-2 text-sm text-n-slate-12">
            <span class="rounded-lg bg-n-solid-1 px-3 py-2"
              >{{ number(row.balance) }} créditos disponíveis</span
            >
            <span
              v-if="row.free_balance !== undefined"
              class="rounded-lg bg-n-solid-1 px-3 py-2"
              >{{ number(row.free_balance) }} gratuitos ·
              {{ number(row.paid_balance) }} comprados</span
            >
          </div>
          <p v-if="row.renews_at" class="mb-3 text-xs text-n-slate-11">
            Franquia de {{ number(row.monthly_allowance) }} por mês, sem
            acumular. Renova em
            {{ row.renews_at.split('-').reverse().join('/') }}. Usamos os
            gratuitos primeiro.
          </p>
          <p
            v-if="!row.configured || !row.product"
            class="mb-3 text-sm text-n-slate-11"
          >
            Consulta indisponível. A integração precisa ser configurada pelo
            administrador.
          </p>
          <div class="flex flex-wrap items-center gap-3">
            <Button
              :label="`Consultar · ${row.product?.credits_per_use || 1} crédito(s)`"
              :disabled="loading || !!consulting || !canConsult(row)"
              :is-loading="consulting === row.id"
              @click="consult(row)"
            />
            <RouterLink
              v-if="
                row.product &&
                row.balance < row.product.credits_per_use &&
                row.administrator
              "
              :to="accountScopedRoute('talkvia_shop')"
              target="_blank"
              rel="noopener noreferrer"
              class="rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-brand"
              >Comprar no marketplace ↗</RouterLink
            >
            <span
              v-else-if="
                row.product && row.balance < row.product.credits_per_use
              "
              class="text-sm text-n-slate-11"
              >Solicite créditos ao administrador.</span
            >
          </div>
        </template>
      </article>
      <p
        v-if="error"
        role="alert"
        class="rounded-xl bg-n-ruby-2 p-3 text-sm text-n-ruby-11"
      >
        {{ error }}
      </p>
      <div
        v-if="result"
        role="status"
        class="rounded-xl border border-n-weak bg-n-solid-2 p-4 text-sm text-n-slate-12"
      >
        <strong>Consulta concluída</strong>
        <p class="mt-2">{{ result.nome }}</p>
        <p v-if="result.data_nascimento">
          Nascimento:
          {{ result.data_nascimento.split('-').reverse().join('/') }}
        </p>
        <p v-if="result.nome_mae">Nome da mãe: {{ result.nome_mae }}</p>
        <p class="mt-2 text-n-slate-11">
          Confira os dados no formulário. Caso o birô não retorne um celular,
          preencha-o para cadastrar o contato e consultar os bancos.
        </p>
      </div>
    </div>
    <template #footer
      ><Button
        label="Voltar à consulta CLT"
        color="slate"
        variant="outline"
        @click="dialog.close()"
    /></template>
  </Dialog>
</template>
